import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import BrandLogo from './BrandLogo';

const { FiChevronDown, FiMenu, FiPhone, FiX } = FiIcons;

const services = [
  { name: 'Realizzazione Siti Web', path: '/servizi/realizzazione-siti-web-napoli' },
  { name: 'E-commerce', path: '/servizi/realizzazione-ecommerce-napoli' },
  { name: 'Social Media Manager', path: '/servizi/social-media-manager-napoli' },
  { name: 'Web Marketing', path: '/servizi/web-marketing-napoli' },
  { name: 'Consulenza SEO', path: '/servizi/consulenza-seo-napoli' },
  { name: 'Branding', path: '/servizi/branding-napoli' },
  { name: 'Intelligenza Artificiale', path: '/servizi/intelligenza-artificiale-napoli' }
];

const navItems = [
  { name: 'Home', path: '/' },
  { name: 'Chi Siamo', path: '/chi-siamo' },
  { name: 'Contatti', path: '/contatti' }
];

function ServicesDropdown({ open, setOpen }) {
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="flex items-center gap-1 font-semibold text-napoli-blu transition-colors hover:text-napoli-azzurro"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen(!open)}
      >
        Servizi
        <SafeIcon
          icon={FiChevronDown}
          className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="absolute left-0 top-full z-50 mt-3 w-72 overflow-hidden rounded-2xl border border-napoli-azzurro/10 bg-white py-2 shadow-2xl"
          >
            {services.map((service) => (
              <Link
                key={service.path}
                to={service.path}
                onClick={() => setOpen(false)}
                className="block border-l-4 border-transparent px-5 py-3 text-sm font-medium text-napoli-blu transition-colors hover:border-napoli-azzurro hover:bg-napoli-light"
              >
                {service.name}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MobileMenu({ close }) {
  return (
    <div className="py-3">
      {navItems.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          onClick={close}
          className="block px-6 py-3 font-semibold text-napoli-blu hover:bg-napoli-light"
        >
          {item.name}
        </Link>
      ))}
      <div className="border-y border-napoli-azzurro/15 bg-napoli-light px-6 py-3 font-bold text-napoli-blu">
        Servizi
      </div>
      {services.map((service) => (
        <Link
          key={service.path}
          to={service.path}
          onClick={close}
          className="block px-8 py-2.5 text-sm font-medium text-napoli-blu hover:bg-napoli-light"
        >
          {service.name}
        </Link>
      ))}
      <div className="space-y-3 border-t border-napoli-azzurro/15 p-5">
        <a
          href="tel:+393500703118"
          className="flex items-center justify-center gap-2 rounded-xl bg-napoli-azzurro px-4 py-3 font-bold text-white"
        >
          <SafeIcon icon={FiPhone} className="h-5 w-5" />
          +39 350 070 3118
        </a>
        <Link
          to="/contatti"
          onClick={close}
          className="block rounded-xl bg-napoli-accent px-4 py-3 text-center font-bold text-napoli-blu"
        >
          Richiedi Preventivo
        </Link>
      </div>
    </div>
  );
}

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-shadow ${isScrolled ? 'bg-white shadow-xl' : 'bg-white/95 shadow-lg backdrop-blur-md'}`}>
      <div className="container mx-auto px-4">
        <nav className="flex items-center justify-between py-3" aria-label="Menu principale">
          <BrandLogo />
          <div className="hidden items-center gap-7 lg:flex">
            <Link
              to="/"
              className={`font-semibold transition-colors hover:text-napoli-azzurro ${location.pathname === '/' ? 'text-napoli-azzurro' : 'text-napoli-blu'}`}
            >
              Home
            </Link>
            <ServicesDropdown open={servicesOpen} setOpen={setServicesOpen} />
            {navItems.slice(1).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`font-semibold transition-colors hover:text-napoli-azzurro ${location.pathname === item.path ? 'text-napoli-azzurro' : 'text-napoli-blu'}`}
              >
                {item.name}
              </Link>
            ))}
          </div>
          <div className="hidden items-center gap-3 md:flex">
            <a
              href="tel:+393500703118"
              className="flex items-center gap-2 rounded-lg bg-napoli-azzurro px-4 py-2 font-semibold text-white transition-colors hover:bg-blue-500"
              aria-label="Chiama ora"
            >
              <SafeIcon icon={FiPhone} className="h-4 w-4" />
              <span className="hidden xl:inline">+39 350 070 3118</span>
            </a>
            <Link
              to="/contatti"
              className="rounded-xl bg-napoli-accent px-5 py-3 font-bold text-napoli-blu shadow-md transition hover:scale-105 hover:bg-yellow-400"
            >
              Richiedi Preventivo
            </Link>
          </div>
          <button
            type="button"
            className="rounded-xl p-2 text-napoli-blu hover:bg-napoli-light lg:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Chiudi menu' : 'Apri menu'}
            aria-expanded={mobileOpen}
          >
            <SafeIcon icon={mobileOpen ? FiX : FiMenu} className="h-6 w-6" />
          </button>
        </nav>
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden rounded-2xl border border-napoli-azzurro/10 bg-white shadow-xl lg:hidden"
            >
              <MobileMenu close={() => setMobileOpen(false)} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;